import type {
  ToggleGroupItem,
  ToggleGroupSelectedValue,
} from '@latitude/toggle-group';
import { ToggleGroup } from '@latitude/toggle-group';
import { FormComponentWrapper } from 'components/FormComponentWrapper';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { FormToggleGroupProps } from './types';

export const FormToggleGroup = <T extends FieldValues = FieldValues>({
  control,
  name,
  description,
  label,
  message,
  wrapperClassname,
  type,
  items,
  disabled,
  variant,
  selected,
  components,
  onChange: passedOnChange,
  rules,
  loading,
  ...toggleGroupProps
}: FormToggleGroupProps<T>) => {
  return (
    <Controller
      name={name}
      {...(control
        ? { control }
        : {
            // if undefined, Controller reads from FormProvider
          })}
      rules={rules}
      render={({
        field: { onBlur, onChange: _onChange, value },
        fieldState: { error },
      }) => {
        /*
        this logic might seems confusing, but the toggle group needs to be
        refactored to change the item onClick in favor of the component onChange
        */
        const handleOnChange = (
          selectedValueItem: ToggleGroupSelectedValue<T>,
          onClickItem:
            | ((item: ToggleGroupSelectedValue<T>) => void)
            | undefined,
        ) => {
          if (selectedValueItem.value === value) {
            return;
          }
          passedOnChange?.(selectedValueItem.value, selectedValueItem);
          onClickItem?.(selectedValueItem);

          // TODO: Refactor to handle different field value types (string | Date | DateRange | string[] | undefined)
          // Currently commented out because it assumes single-value fields and breaks array fields.
          // Form state management is delegated to either passedOnChange or onClickItem handlers.
          // onChange(selectedValueItem.value);
        };

        const wrappedItems = (items as Array<ToggleGroupItem<T>>).map(
          (item) => {
            const onClickItem = item.onClick;
            return {
              ...item,
              onClick: (selectedValueItem: ToggleGroupSelectedValue<T>) => {
                handleOnChange(selectedValueItem, onClickItem);
              },
            };
          },
        );
        return (
          <FormComponentWrapper
            name={name}
            description={description}
            label={label}
            error={error}
            className={wrapperClassname}
            message={message}
            disabled={disabled}
          >
            <ToggleGroup
              type={type}
              items={wrappedItems}
              variant={variant}
              selected={selected as unknown as ToggleGroupSelectedValue<T>[]}
              components={components}
              onBlur={onBlur}
              loading={loading}
              {...toggleGroupProps}
            />
          </FormComponentWrapper>
        );
      }}
    />
  );
};
